// Single active tab per tenant database.
//
// The OPFS SAH-pool VFS gives one worker exclusive access to the database
// files, so only one tab can have the tenant open. The active tab holds a Web
// Lock; another tab shows "Open in another tab — use here", and "use here"
// asks the active tab to close its database and hand the lock over.

export interface TabLock {
  /** Close the database first, then release. */
  release(): void
}

export interface TabLockOptions {
  /** Another tab asked to take over: close the local store, then call release(). */
  onTakeoverRequested(release: () => void): void
}

const channelName = (name: string) => `${name}:takeover`

function hold(
  name: string,
  mode: { ifAvailable?: boolean },
  options: TabLockOptions,
): Promise<TabLock | null> {
  return new Promise((resolveAcquire) => {
    let releaseLock: () => void = () => {}
    const released = new Promise<void>((r) => (releaseLock = r))
    void navigator.locks
      .request(name, mode, async (lock) => {
        if (!lock) {
          resolveAcquire(null)
          return
        }
        const channel = new BroadcastChannel(channelName(name))
        const release = () => {
          channel.close()
          releaseLock()
        }
        channel.onmessage = (event) => {
          if (event.data === 'takeover') options.onTakeoverRequested(release)
        }
        resolveAcquire({ release })
        await released
      })
      .catch(() => resolveAcquire(null))
  })
}

/** Takes the lock if no other tab has it; resolves null when another tab is active. */
export function tryAcquireTabLock(name: string, options: TabLockOptions): Promise<TabLock | null> {
  return hold(name, { ifAvailable: true }, options)
}

/** Asks the active tab to hand over, then waits for the lock ("use here"). */
export async function takeOverTabLock(name: string, options: TabLockOptions): Promise<TabLock> {
  const channel = new BroadcastChannel(channelName(name))
  channel.postMessage('takeover')
  channel.close()
  const lock = await hold(name, {}, options)
  if (!lock) throw new Error('could not take over the database lock')
  return lock
}

export function tabLockName(userId: string, tenantId: string): string {
  return `ims-db:${userId}:${tenantId}`
}
